import { ABBaseComponent, defineComponent } from './ab-base-component.js';

class ABStickyHeader extends ABBaseComponent {
  connectedCallback() {
    // Create sentinel element for IntersectionObserver
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'ab-header-sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.sentinel.style.cssText = 'position:absolute;top:0;left:0;height:1px;width:1px;pointer-events:none;';
    this.parentElement.insertBefore(this.sentinel, this);

    // Spacer element prevents content jump when header goes fixed
    this.spacer = document.createElement('div');
    this.spacer.className = 'ab-header-spacer';
    this.spacer.setAttribute('aria-hidden', 'true');
    this.spacer.style.cssText = 'display:none;';
    this.parentElement.insertBefore(this.spacer, this.nextSibling);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        const isSticky = !entry.isIntersecting;
        const wasSticky = this.classList.contains('is-sticky');
        this.classList.toggle('is-sticky', isSticky);

        if (isSticky && !wasSticky) {
          // Going fixed — insert spacer to prevent layout shift
          this.spacer.style.display = 'block';
          this.spacer.style.height = this.offsetHeight + 'px';
        } else if (!isSticky && wasSticky) {
          // Returning to flow — remove spacer and clear hidden state
          this.spacer.style.display = 'none';
          this.classList.remove('is-hidden');
        }
        this._syncHeaderOffset();
      },
      { threshold: 0 }
    );
    this.observer.observe(this.sentinel);
    this._syncHeaderOffset();

    // --- Scroll-direction hide/reveal ---
    this.lastScrollY = window.scrollY;
    this.scrollTicking = false;
    this.isHovered = false;
    this.scrollThreshold = 5; // px — ignore tiny movements to prevent flicker

    this._onScroll = this._onScroll.bind(this);
    this._onMouseEnter = this._onMouseEnter.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);

    window.addEventListener('scroll', this._onScroll, { passive: true });
    this.addEventListener('mouseenter', this._onMouseEnter);
    this.addEventListener('mouseleave', this._onMouseLeave);

    // Cart trigger contract: support both data-cart-trigger and data-action="open-cart".
    this.onCartTriggerClick = this.onCartTriggerClick.bind(this);
    document.addEventListener('click', this.onCartTriggerClick);

    // Cart count update listener
    document.addEventListener('cart:updated', () => this.updateCartCount());
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.sentinel?.remove();
    window.removeEventListener('scroll', this._onScroll);
    this.removeEventListener('mouseenter', this._onMouseEnter);
    this.removeEventListener('mouseleave', this._onMouseLeave);
    document.removeEventListener('click', this.onCartTriggerClick);
  }

  _onScroll() {
    if (this.scrollTicking) return;
    this.scrollTicking = true;

    requestAnimationFrame(() => {
      this._updateScrollDirection();
      this.scrollTicking = false;
    });
  }

  _updateScrollDirection() {
    const currentY = window.scrollY;
    const delta = currentY - this.lastScrollY;

    // Only act when sticky and movement exceeds threshold
    if (this.classList.contains('is-sticky') && Math.abs(delta) > this.scrollThreshold) {
      if (delta > 0 && !this.isHovered) {
        // Scrolling DOWN — hide header (unless hovered)
        this.classList.add('is-hidden');
      } else if (delta < 0) {
        // Scrolling UP — reveal header
        this.classList.remove('is-hidden');
      }
      this._syncHeaderOffset();
    }

    this.lastScrollY = currentY;
  }

  // Publish the visible header height so sticky page elements
  // (e.g. the finishes-page swatch index) can sit below it.
  _syncHeaderOffset() {
    let offset = 0;
    if (!this.classList.contains('is-hidden')) {
      const header = this.querySelector('.ab-header');
      offset = header ? header.offsetHeight : 64;
    }
    document.documentElement.style.setProperty('--ab-header-offset', offset + 'px');
  }

  _onMouseEnter() {
    this.isHovered = true;
    // Reveal immediately if hidden while user mouses over
    this.classList.remove('is-hidden');
  }

  _onMouseLeave() {
    this.isHovered = false;
  }

  onCartTriggerClick(event) {
    const trigger = event.target.closest('[data-cart-trigger], [data-action="open-cart"]');
    if (!trigger) return;

    event.preventDefault();
    document.dispatchEvent(new CustomEvent('cart:open'));
  }

  async updateCartCount() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=header`);
      const data = await response.json();
      const parser = new DOMParser();
      const doc = parser.parseFromString(data['header'], 'text/html');
      const newCount = doc.querySelector('[data-cart-count]');
      const currentCount = this.$('[data-cart-count]');
      if (newCount && currentCount) {
        currentCount.textContent = newCount.textContent;
      }
    } catch (e) {
      // Silently fail — cart count will update on next page load
    }
  }
}

defineComponent('ab-sticky-header', ABStickyHeader);
